import * as stylex from '@stylexjs/stylex';
import { useForm, useStore } from '@tanstack/react-form';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Activity, useId } from 'react';
import { DialogTrigger, ListLayout, Virtualizer, type Key } from 'react-aria-components';
import { literal as vLiteral } from 'valibot';

import { FourWordPasswordExplanationDialog } from '@/components/four-word-password-explanation-dialog.component';
import { NonUSDoctorSpecialtyEnum } from '@/libs/openapi';
import {
  ArrowRightStrokeIcon,
  Button,
  Divider,
  GoogleButton,
  Heading,
  IconButton,
  LabeledInput,
  PASSWORD_INDICATOR_SLOTS,
  PasswordIndicator,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  RefreshStrokeIcon,
  Text,
  TextField,
  TOAST_TIMEOUT,
  minDelay,
  LINK_SLOTS,
  SegmentedButton,
  SegmentedButtonGroup,
  View,
  ComboBox,
  Popover,
  ListBox,
  ListBoxItem,
  Icon,
  Hint,
  TextButton,
  Checkbox,
  LIST_BOX_ITEM_SLOTS,
  SKELETON_MIN_DELAY_DURATION,
  useDebounce,
  ProgressIndicator,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';
import { RouterTextButtonLink } from '@/libs/tanstack-router/components/router-text-button-link.component';
import { authMutations } from '@/resources/auth/auth.api';
import { locationQueries } from '@/resources/location/location.api';
import { ProfileSchema } from '@/resources/profile/profile.schema';
import { specialtyQuery } from '@/resources/specialty/specialty.api';
import { userMutations, userQueries } from '@/resources/user/user.api';
import { UserSchema } from '@/resources/user/user.schema';
import { suggestPassword } from '@/utils/suggest-password.util';
import { tryCatch } from '@/utils/try-catch.util';

export const Route = createFileRoute('/_auth/sign-up')({
  component: RouteComponent,
  beforeLoad: () => {
    if (localStorage.getItem('access_token')) {
      throw redirect({ to: '/' });
    }
  },
});

function RouteComponent() {
  const id = useId();
  const { toastQueue } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const match = Route.useMatch();

  const registerMutation = useMutation(userMutations.register);
  const googleLoginMutation = useMutation(authMutations.googleLogin);

  const form = useForm({
    defaultValues: {
      email: '',
      password: suggestPassword(),
      full_name: '',
      health_care_provider: true,
      country_code: null as Key | null,
      region_code: null as Key | null,
      npi_number: '',
      has_npi_number: true,
      doctor_specialty: '' as Key | null,
      newsletter: false,
      profession: '',
      hasAgreedToTerms: false,
      locationSearch: '',
    },
    onSubmit: async ({ value }) => {
      const { error } = await minDelay(tryCatch(registerMutation.mutateAsync({
        email: value.email.trim(),
        password: value.password.trim(),
        full_name: value.full_name.trim(),
        health_care_provider: value.health_care_provider,
        country_code: value.country_code!.toString(),
        region_code: value.region_code ? value.region_code.toString() : undefined,
        npi_number: value.npi_number === '' ? undefined : Number(value.npi_number),
        doctor_specialty: value.doctor_specialty
          ? value.country_code === 'US'
            ? value.doctor_specialty.toString()
            : NonUSDoctorSpecialtyEnum[value.doctor_specialty.toString() as keyof typeof NonUSDoctorSpecialtyEnum]
          : undefined,
        profession: value.profession ? value.profession.trim() : undefined,
        newsletter: value.newsletter,
      })), PROGRESS_INDICATOR_MIN_DELAY_DURATION);

      if (!error) {
        navigate({ to: '/sign-in' });
        toastQueue.add({ message: 'Check your email inbox and click on the link we sent to confirm registration.', variant: 'success' }, { timeout: TOAST_TIMEOUT });
      }
    },
  });

  const debouncedPassword = useDebounce(useStore(form.store, (state) => state.values.password), PROGRESS_INDICATOR_MIN_DELAY_DURATION);
  const getPasswordStrengthQuery = useQuery({
    ...userQueries.getPasswordStrength({ password: debouncedPassword }),
    enabled: debouncedPassword !== form.options.defaultValues?.password,
    placeholderData: keepPreviousData,
  });

  const debouncedLocation = useDebounce(useStore(form.store, (state) => state.values.locationSearch), SKELETON_MIN_DELAY_DURATION);
  const locationsQuery = useQuery(locationQueries.get({ search: debouncedLocation }));

  const npiNumber = useStore(form.store, (state) => state.values.npi_number);
  const getSpecialtyByNpiQuery = useQuery(specialtyQuery.getByNpi({ npi_number: npiNumber }));

  const isEmailDirty = useStore(form.store, (state) => state.fieldMeta?.email?.isDirty);

  return (
    <>
      <div {...stylex.props(styles.base, isEmailDirty && styles.withFullFormShowed)}>
        <form
          id={id}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          {...stylex.props(formStyles.base)}>
          <Heading level={1} size={28}>Create Reader account</Heading>
          <form.Field name="email" validators={{ onMount: UserSchema.email, onSubmit: UserSchema.email }}>
            {(field) => {
              const isDirtyAndInvalid = field.getMeta().isDirty && field.state.meta.errors.length > 0;

              return (
                <TextField
                  autoComplete="off"
                  autoFocus
                  id={field.name}
                  isInvalid={isDirtyAndInvalid}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}>
                  <LabeledInput label="Email" />
                  {isDirtyAndInvalid &&
                    <Hint>
                      <Icon />
                      {field.state.meta.errors[0]?.message}
                    </Hint>}
                </TextField>
              );
            }}
          </form.Field>

          <form.Subscribe selector={(state) => state.fieldMeta?.email?.isDirty}>
            {(isEmailDirty) =>
              <Activity mode={isEmailDirty ? 'visible' : 'hidden'}>
                <div {...stylex.props(passwordStyles.fieldContainer)}>
                  <form.Field name="password" validators={{ onSubmit: UserSchema.password }}>
                    {(field) =>
                      <>
                        <TextField
                          autoComplete="off"
                          id={field.name}
                          isInvalid={field.state.meta.errors.length > 0}
                          name={field.name}
                          type="text"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={field.handleChange}>
                          <LabeledInput label="Password">
                            {(renderProps) =>
                              <>
                                {renderProps.defaultChildren}
                                {field.state.value.length > 0 &&
                                  <IconButton
                                    aria-label="Generate new password"
                                    excludeFromTabOrder
                                    size={32}
                                    variant="ghost"
                                    onPress={() => field.handleChange(suggestPassword())}>
                                    <RefreshStrokeIcon />
                                  </IconButton>}
                              </>}
                          </LabeledInput>
                          {field.state.meta.errors.length > 0 &&
                            <Hint>
                              <Icon />
                              {field.state.meta.errors[0]?.message}
                            </Hint>}
                        </TextField>
                        <PasswordIndicator style={passwordStyles.indicator} value={getPasswordStrengthQuery.data ?? 4}>
                          {(renderProps) =>
                            <>
                              {renderProps.defaultChildren}
                              <Text slot={PASSWORD_INDICATOR_SLOTS.DESCRIPTION}>{renderProps.valueText}</Text>
                            </>}
                        </PasswordIndicator>
                        <DialogTrigger>
                          <TextButton size={28} style={textButtonStyles.base} variant="tertiary">
                            Why is there a pre-generated four-word password?
                          </TextButton>
                          <FourWordPasswordExplanationDialog />
                        </DialogTrigger>
                      </>}
                  </form.Field>
                </div>
              </Activity>}
          </form.Subscribe>

          <form.Subscribe selector={(state) => state.fieldMeta?.email?.isDirty}>
            {(isEmailDirty) =>
              <Activity mode={isEmailDirty ? 'visible' : 'hidden'}>
                <form.Field name="full_name" validators={{ onSubmit: ProfileSchema.fullName }}>
                  {(field) =>
                    <TextField
                      autoComplete="off"
                      id={field.name}
                      isInvalid={field.state.meta.errors.length > 0}
                      name={field.name}
                      type="text"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={field.handleChange}>
                      <LabeledInput label="Full name" />
                      {field.state.meta.errors.length > 0 &&
                        <Hint>
                          <Icon />
                          {field.state.meta.errors[0]?.message}
                        </Hint>}
                    </TextField>}
                </form.Field>

                <form.Field name="health_care_provider">
                  {(field) =>
                    <View style={segmentedButtonGroupStyles.container}>
                      <SegmentedButtonGroup
                        selectedKeys={[field.state.value ? 'yes' : 'no']}
                        selectionMode="single"
                        size={40}
                        onSelectionChange={(keys) => {
                          field.handleChange(keys.keys().next().value === 'yes');
                          field.form.resetField('doctor_specialty');
                          field.form.resetField('profession');
                          field.form.resetField('has_npi_number');
                          field.form.resetField('npi_number');
                        }}>
                        <SegmentedButton id="yes" size={40}>Yes</SegmentedButton>
                        <SegmentedButton id="no" size={40}>No</SegmentedButton>
                      </SegmentedButtonGroup>
                      <Text color="tertiary" size={16}>Are you a Healthcare Professional?</Text>
                    </View>}
                </form.Field>

                <form.Field name="country_code" validators={{ onSubmit: ProfileSchema.countryCode }}>
                  {(field) => {
                    const isTouchedAndInvalid = field.getMeta().isTouched && field.state.meta.errors.length > 0;

                    return (
                      <ComboBox
                        allowsEmptyCollection
                        defaultFilter={() => true} // To return all regions if the country name is entered
                        id={field.name}
                        isInvalid={isTouchedAndInvalid}
                        name={field.name}
                        selectedKey={field.form.state.values.region_code ? `${field.state.value}-${field.form.state.values.region_code}` : field.state.value}
                        size={48}
                        onBlur={field.handleBlur}
                        onInputChange={(value) => {
                          // Only start searching for a location if the value has at least 3 characters, so that not too many results are rendered at once
                          if (value && value.length >= 3 && value !== field.state.value) {
                            field.form.setFieldValue('locationSearch', value);
                          }
                        }}
                        onSelectionChange={(key) => {
                          if (!key) {
                            return;
                          }

                          const [countryCode, regionCode] = key.toString().split('-');

                          if (field.state.value === 'US' || countryCode === 'US') {
                            field.form.resetField('doctor_specialty');
                          }

                          if (countryCode !== 'US') {
                            field.form.resetField('npi_number');
                            field.form.resetField('has_npi_number');
                          }

                          field.form.setFieldValue('region_code', typeof regionCode === 'string' ? regionCode : null);

                          field.handleChange(countryCode);
                        }}>
                        {(renderProps) =>
                          <>
                            <LabeledInput label="Location">
                              {(renderProps) => renderProps.defaultChildren}
                            </LabeledInput>

                            {isTouchedAndInvalid &&
                              <Hint>
                                <Icon />
                                {field.state.meta.errors[0]?.message}
                              </Hint>}
                            <Popover isOpen={renderProps.isOpen && !!locationsQuery.data?.length}>
                              <Virtualizer
                                layout={ListLayout}
                                layoutOptions={{
                                  rowHeight: 40,
                                  padding: 0,
                                  dropIndicatorThickness: 0,
                                  gap: 0,
                                }}>
                                <ListBox items={locationsQuery.data ?? []}>
                                  {({ name, code, regionName, regionCode }) =>
                                    <ListBoxItem
                                      id={regionCode ? `${code}-${regionCode}` : code}
                                      size={40}
                                      textValue={`${regionName || name}`}>
                                      {regionName ?
                                        <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>
                                          {regionName}&nbsp;
                                          <Text color="quaternary" size={12}>{name}</Text>
                                        </Text> :
                                        <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>{name}</Text>}
                                    </ListBoxItem>}
                                </ListBox>
                              </Virtualizer>
                            </Popover>
                          </>}
                      </ComboBox>
                    );
                  }}
                </form.Field>

                <form.Subscribe selector={(state) => [state.values.country_code, state.values.health_care_provider, state.values.has_npi_number] as const}>
                  {([country_code, isHCP, hasNPINumber]) => country_code ? isHCP ?
                    <>
                      {country_code === 'US' &&
                        <>
                          <View style={npiNumberStyles.container}>
                            <form.Field
                              name="npi_number"
                              validators={{
                                onChangeAsyncDebounceMs: 400,
                                onChangeAsync: async ({ value, fieldApi }) => {
                                  if (!fieldApi.form.state.values.has_npi_number) {
                                    return;
                                  }

                                  if (!value) {
                                    return { message: 'Can\'t be empty' };
                                  }

                                  if (value.length !== 10) {
                                    return { message: 'Enter a valid NPI number' };
                                  }

                                  const { data } = await tryCatch(getSpecialtyByNpiQuery.refetch());

                                  if (!data?.data) {
                                    return { message: 'Enter valid NPI number' };
                                  }
                                },
                              }}>
                              {(field) =>
                                <TextField
                                  autoComplete="off"
                                  id={field.name}
                                  isDisabled={!hasNPINumber}
                                  isInvalid={field.state.meta.errors.length > 0}
                                  name={field.name}
                                  size={48}
                                  style={npiNumberStyles.field}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={field.handleChange}>
                                  <LabeledInput label="NPI number">
                                    {(renderProps) =>
                                      <>
                                        {renderProps.defaultChildren}
                                        {getSpecialtyByNpiQuery.isLoading && <ProgressIndicator />}
                                      </>}
                                  </LabeledInput>
                                  {field.state.meta.errors.length > 0 &&
                                    <Hint>
                                      <Icon />
                                      {field.state.meta.errors[0]?.message}
                                    </Hint>}
                                </TextField>}
                            </form.Field>

                            <form.Field name="has_npi_number">
                              {(field) =>
                                <TextButton
                                  style={npiNumberStyles.noNpiTextButton}
                                  variant="tertiary"
                                  onPress={() => {
                                    field.handleChange(!field.state.value);
                                    field.form.resetField('npi_number');
                                    field.form.resetField('doctor_specialty');
                                  }}>
                                  {field.state.value ? 'I don’t have an NPI number' : 'Use NPI number'}
                                </TextButton>}
                            </form.Field>
                          </View>
                          {getSpecialtyByNpiQuery.data &&
                            <TextField isDisabled value={getSpecialtyByNpiQuery.data ?? ''}>
                              <LabeledInput label="Specialty" />
                            </TextField>}
                        </>}

                      {country_code && !(country_code === 'US' && hasNPINumber) &&
                        <form.Field name="doctor_specialty" validators={{ onSubmit: ProfileSchema.specialty }}>
                          {(field) => {
                            return country_code === 'US' ?
                              <TextField
                                autoComplete="off"
                                id={field.name}
                                isInvalid={field.state.meta.errors.length > 0}
                                name={field.name}
                                type="text"
                                value={field.state.value?.toString()}
                                onBlur={field.handleBlur}
                                onChange={field.handleChange}>
                                <LabeledInput label="Specialty" />
                                {field.state.meta.errors.length > 0 &&
                                  <Hint>
                                    <Icon />
                                    {field.state.meta.errors[0]?.message}
                                  </Hint>}
                              </TextField> :
                              <ComboBox
                                id={field.name}
                                isInvalid={field.state.meta.errors.length > 0}
                                name={field.name}
                                selectedKey={field.state.value}
                                size={48}
                                onBlur={field.handleBlur}
                                onSelectionChange={(key) => {
                                  field.handleChange(key);
                                }}>
                                <LabeledInput label="Specialty" />
                                <Popover>
                                  <ListBox items={Object.entries(NonUSDoctorSpecialtyEnum)}>
                                    {([key, value]) => <ListBoxItem id={key} textValue={value}>{value}</ListBoxItem>}
                                  </ListBox>
                                </Popover>
                                {field.state.meta.errors.length > 0 &&
                                  <Hint>
                                    <Icon />
                                    {field.state.meta.errors[0]?.message}
                                  </Hint>}
                              </ComboBox>;
                          }}
                        </form.Field>}
                    </> :
                    <form.Field name="profession" validators={{ onSubmit: ProfileSchema.profession }}>
                      {(field) =>
                        <TextField
                          autoComplete="off"
                          id={field.name}
                          isInvalid={field.state.meta.errors.length > 0}
                          name={field.name}
                          type="text"
                          value={field.state.value?.toString()}
                          onBlur={field.handleBlur}
                          onChange={field.handleChange}>
                          <LabeledInput label="Profession" />
                          {field.state.meta.errors.length > 0 &&
                            <Hint>
                              <Icon />
                              {field.state.meta.errors[0]?.message}
                            </Hint>}
                        </TextField>}
                    </form.Field> : null
                  }
                </form.Subscribe>

                <form.Field name="hasAgreedToTerms" validators={{ onSubmit: vLiteral(true) }}>
                  {(field) => {
                    const isTouchedAndInvalid = field.getMeta().isTouched && field.state.meta.errors.length > 0;

                    return (
                      <Checkbox
                        id={field.name}
                        isInvalid={isTouchedAndInvalid}
                        isSelected={field.state.value}
                        name={field.name}
                        onBlur={field.handleBlur}
                        onChange={field.handleChange}>
                        I agree to the TrendMD <RouterLink _style={linkStyles.base} size={16} target="_blank" to="/privacy-and-terms/$">Privacy and Terms</RouterLink>
                      </Checkbox>
                    );
                  }}
                </form.Field>

                <form.Field name="newsletter">
                  {(field) =>
                    <Checkbox
                      id={field.name}
                      isSelected={field.state.value}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={field.handleChange}>
                      I opt-in to receive TrendMD newsletter and personalized recommendations
                    </Checkbox>}
                </form.Field>

                <form.Subscribe
                  selector={(state) => [state.isSubmitting] as const}>
                  {([isSubmitting]) =>
                    <Button
                      form={id}
                      isPending={isSubmitting}
                      isRounded
                      size={48}
                      type="submit">
                      Sign up
                    </Button>}
                </form.Subscribe>
              </Activity>}
          </form.Subscribe>
        </form>

        <form.Subscribe selector={(state) => state.fieldMeta?.email?.isDirty}>
          {(isEmailDirty) => !isEmailDirty && (
            <>
              <div {...stylex.props(dividerStyles.container)}>
                <Divider size={0.5} variant="silent" />
                <Text color="tertiary" size={13}>or</Text>
                <Divider size={0.5} variant="silent" />
              </div>

              <GoogleButton
                isRounded
                size={52}
                variant="alternative"
                onPress={() => {
                  sessionStorage.setItem('google_login_url', match.fullPath);
                  googleLoginMutation.mutate(void 0, { onSuccess: (data) => window.location.href = data.auth_url });
                }}>
                Continue with Google
              </GoogleButton>

              <Text color="tertiary" size={16} style={typographyStyles.textAction}>
                Already a member?
                <RouterLink size={16} to="/sign-in">
                  Sign in
                  <ArrowRightStrokeIcon color="interactive" size={16} slot={LINK_SLOTS.RIGHT_ACCESSORY} />
                </RouterLink>
              </Text>
            </>)}
        </form.Subscribe>
      </div>
      <form.Subscribe selector={(state) => state.fieldMeta?.email?.isDirty}>
        {(isEmailDirty) => !isEmailDirty && (
          <footer {...stylex.props(footerStyles.base)}>
            <RouterTextButtonLink safeArea="square" size={28} target="_blank" to="/privacy-and-terms/$" variant="tertiary">Privacy and terms</RouterTextButtonLink>
          </footer>)}
      </form.Subscribe>
    </>
  );
}

const styles = stylex.create({
  base: {
    display: 'flex',
    flex: '1 0 0',
    flexDirection: 'column',
    gap: 32,
    maxWidth: 432,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: {
      default: 120,
      [breakpoints.large]: 140,
    },
    width: '100%',
  },
  withFullFormShowed: {
    gap: 16,
    paddingBottom: {
      default: 12,
      [breakpoints.large]: 32,
    },
    paddingTop: {
      default: 12,
      [breakpoints.large]: 32,
    },
  }
});

const dividerStyles = stylex.create({
  container: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
  },
});

const passwordStyles = stylex.create({
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  indicator: {
    paddingInline: 16,
    width: '100%',
  },
});

const typographyStyles = stylex.create({
  textAction: {
    display: 'inline-flex',
    gap: 8,
  }
});

const textButtonStyles = stylex.create({
  base: {
    marginBottom: 8,
    marginLeft: 14,
    width: 'fit-content',
  },
});

const formStyles = stylex.create({
  base: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
});

const segmentedButtonGroupStyles = stylex.create({
  container: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 12,
  },
});

const npiNumberStyles = stylex.create({
  container: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  field: {
    width: '100%',
  },
  noNpiTextButton: {
    color: $.textQuaternary,
    marginLeft: 14,
  },
});

const linkStyles = stylex.create({
  base: {
    color: 'inherit',
    textDecoration: 'underline',
  },
});

const footerStyles = stylex.create({
  base: {
    alignContent: 'flex-start',
    alignItems: 'flex-start',
    bottom: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    paddingBlock: 2,
    width: '100%',
  },
});
